/**
 * CommandColumn — the workspace's sticky left rail: care score, the six
 * pillar bars, and the FULL prioritised Do-next list. Merges what the old
 * Overview command row and the Care tab both rendered, so the score/action
 * duplication dies here. Log Contact + Refresh live here now, and the
 * client's social profile copy sits above Links.
 */

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "@/core/store/useAppStore";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { useCareScore, CareScoreData } from "../hooks/useCareScore";
import { AddLinkModal } from "./AddLinkModal";
import { HomeSocialProfiles } from "./HomeSocialProfiles";
import { getActions, Action } from "../careActions";
import { resolveActionLink } from "../homeLinks";
import {
  ChatIcon,
  CheckIcon,
  ChevronRightIcon,
  LinkIcon,
  PlusIcon,
  RefreshIcon,
  SpinnerIcon,
} from "@/components/icons";

const PILLARS: { key: keyof CareScoreData; label: string }[] = [
  { key: "runwayScore", label: "Runway" },
  { key: "contentScore", label: "Content" },
  { key: "serviceScore", label: "Service" },
  { key: "contactScore", label: "Contact" },
  { key: "planningScore", label: "Planning" },
  { key: "growthScore", label: "Growth" },
];

const ACTION_DOT: Record<Action["impact"], string> = {
  high: "bg-rose-500",
  medium: "bg-amber-500",
  low: "bg-gray-300",
};

const scoreColor = (score: number) =>
  score >= 80 ? "text-emerald-600" : score >= 60 ? "text-amber-600" : "text-rose-600";

const barColor = (score: number) =>
  score >= 80 ? "bg-emerald-500" : score >= 60 ? "bg-amber-500" : "bg-rose-500";

export const CommandColumn: React.FC<{ clientId: string }> = ({ clientId }) => {
  const navigate = useNavigate();
  const { selectedClient } = useAppStore();
  const { data, trend, loading, refresh, logContact } = useCareScore({ clientId });
  // Guard on id so a stale store selection never shows another client's
  // profile copy or links.
  const client = selectedClient?.id === clientId ? selectedClient : null;
  // Quick access to the client's own resources (IG, website, drive, ...).
  const links = client?.links ?? [];
  const [loggingContact, setLoggingContact] = useState(false);
  const [contactLogged, setContactLogged] = useState(false);
  const [addLinkOpen, setAddLinkOpen] = useState(false);

  const actions = useMemo(() => (data ? getActions(data) : []), [data]);
  const highCount = actions.filter((a) => a.impact === "high").length;
  const delta = trend?.delta ?? 0;

  const handleLogContact = async () => {
    setLoggingContact(true);
    try {
      await logContact();
      setContactLogged(true);
    } finally {
      setLoggingContact(false);
    }
  };

  const goAction = (action: Action) => {
    const to = resolveActionLink(action.link, clientId);
    if (to) navigate(to);
  };

  return (
    <div className="flex flex-col gap-4">
      <section className="p-5 rounded-2xl bg-white">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-[0.08em]">
            Care score
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleLogContact}
              disabled={loggingContact}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {loggingContact ? (
                <SpinnerIcon className="w-3.5 h-3.5" />
              ) : contactLogged ? (
                <CheckIcon className="w-3.5 h-3.5 text-emerald-600" />
              ) : (
                <ChatIcon className="w-3.5 h-3.5" />
              )}
              {contactLogged ? "Logged" : "Log contact"}
            </button>
            <button
              type="button"
              onClick={refresh}
              aria-label="Refresh care score"
              className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <RefreshIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
        {loading || !data ? (
          <div
            className="flex items-center justify-center py-10"
            data-testid="care-loading"
          >
            <SpinnerIcon className="w-5 h-5 text-gray-400" />
          </div>
        ) : (
          <>
            <div className="flex items-baseline gap-3">
              <span className={`text-5xl font-semibold leading-none ${scoreColor(data.score)}`}>
                {data.score}
              </span>
              {trend && (
                <span className="text-xs font-medium text-gray-400">
                  {delta > 0 ? "▲" : delta < 0 ? "▼" : "■"} {Math.abs(delta)} this week
                </span>
              )}
            </div>
            <p
              className={`mt-2 text-sm font-medium ${
                highCount > 0 ? "text-rose-600" : "text-emerald-600"
              }`}
            >
              {highCount > 0
                ? `${highCount} need${highCount === 1 ? "s" : ""} your attention`
                : "On track"}
            </p>
            <dl className="mt-4 space-y-2.5">
              {PILLARS.map(({ key, label }) => {
                const value = data[key] as number;
                return (
                  <div key={key} className="flex items-center gap-3">
                    <dt className="w-20 shrink-0 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400">
                      {label}
                    </dt>
                    <dd className="flex-1 flex items-center gap-2 min-w-0">
                      <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${barColor(value)}`}
                          style={{ width: `${Math.max(2, Math.min(100, value))}%` }}
                        />
                      </div>
                      <span className="w-7 text-right text-xs font-semibold text-gray-600 tabular-nums">
                        {value}
                      </span>
                    </dd>
                  </div>
                );
              })}
            </dl>
          </>
        )}
      </section>

      <section className="p-4 rounded-2xl bg-white">
        <div className="flex items-center justify-between px-1 mb-2">
          <h3 className="text-sm font-semibold text-gray-900">Do next</h3>
          {actions.length > 0 && (
            <span className="text-xs text-gray-400">{actions.length} to act on</span>
          )}
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <SpinnerIcon className="w-5 h-5 text-gray-400" />
          </div>
        ) : actions.length === 0 ? (
          <div className="flex items-center gap-2 px-1 py-4 text-sm text-gray-500">
            <CheckIcon className="w-4 h-4 text-emerald-500" />
            Nothing needs you right now.
          </div>
        ) : (
          <div className="space-y-0.5">
            {actions.map((action, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goAction(action)}
                disabled={!action.link}
                className={`group w-full text-left flex items-center gap-3 px-2.5 py-2 rounded-xl transition-colors ${
                  action.link ? "hover:bg-gray-50 cursor-pointer" : "cursor-default"
                }`}
              >
                <span className={`w-2 h-2 rounded-full shrink-0 ${ACTION_DOT[action.impact]}`} />
                <span className="flex-1 text-sm text-gray-800 leading-snug">{action.label}</span>
                {action.link && (
                  <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-gray-500 shrink-0" />
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Keyed so a client switch drops an open draft instead of saving it
          onto the newly selected client. */}
      {client && <HomeSocialProfiles key={client.id} client={client} />}

      <section className="p-4 rounded-2xl bg-white">
        <div className="flex items-center justify-between px-1 mb-1">
          <h3 className="text-sm font-semibold text-gray-900">Links</h3>
          <PanelHeaderAction
            label="Add"
            ariaLabel="Add link"
            icon={<PlusIcon className="w-4 h-4" />}
            onClick={() => setAddLinkOpen(true)}
          />
        </div>
        {links.length === 0 ? (
          <p className="px-1 py-2 text-sm text-gray-500">
            No links yet. Add the client's Instagram, website, or drive.
          </p>
        ) : (
          <div className="divide-y divide-gray-100">
            {links.map((link) => (
              <a
                key={`${link.name}-${link.url}`}
                href={link.url}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-2.5 px-1 py-2 text-sm text-gray-800 hover:text-blue-600 transition-colors"
              >
                <LinkIcon className="w-3.5 h-3.5 text-gray-300 group-hover:text-blue-400 shrink-0" />
                <span className="truncate">{link.name}</span>
              </a>
            ))}
          </div>
        )}
      </section>

      <AddLinkModal
        isOpen={addLinkOpen}
        onClose={() => setAddLinkOpen(false)}
        clientId={clientId}
        links={links}
      />
    </div>
  );
};
